import { motion, AnimatePresence } from 'framer-motion';
import React, { useState } from 'react';

const ConfigurationPanel = ({
  selectedBot,
  configuration, // Receive current config state
  onConfigurationChange, // Receive handler to update parent state
  onDeploy,
  isDeploying,
  isDisabled // General disabled state (not connected)
}) => {

const handleChange = (key, value) => {
  onConfigurationChange({ [key]: value });
};

// Specific handler for username selection to clear custom input if needed
const handleUsernameTypeChange = (e) => {
  const newType = e.target.value;
  const update = { botUsername: newType };
  // If changing *away* from custom, clear the custom value
  if (newType !== 'custom' && configuration.customBotUsername) {
    update.customBotUsername = ''; // Reset custom username
  }
  onConfigurationChange(update);
};

if (!selectedBot) {
  return (
    <div className={`bg-dark-600 rounded-2xl p-6 h-full flex items-center justify-center text-gray-500 border border-dark-400 ${isDisabled ? 'opacity-50' : ''}`}>
      Select a bot to configure.
    </div>
  );
}

const accentColor = selectedBot.accentColor || 'purple';

return (
  <div className={`bg-dark-600 rounded-2xl p-6 gradient-bg-${accentColor} h-full flex flex-col transform-gpu hover:scale-[1.005] transition-medium border border-dark-400 hover:border-accent-${accentColor}/30 ${isDisabled || isDeploying ? 'opacity-70 pointer-events-none' : ''}`}>
    <h3 className="font-bold text-lg mb-5 flex items-center group relative z-10">
      <div className={`w-8 h-8 rounded-lg bg-accent-${accentColor}/20 flex items-center justify-center mr-2 transform-gpu group-hover:rotate-12 transition-medium`}>
        <i className={`fas fa-sliders-h text-accent-${accentColor}`}></i>
      </div>
      {selectedBot.name} Configuration
    </h3>

    <div className="flex-1 space-y-5 relative z-10"> {/* Use flex-1 to push button down */}
      {/* Bot Username Selection */}
      <div className="transform-gpu transition-medium hover:scale-[1.02]">
        <label className="block text-xs text-gray-400 mb-1">Bot Username</label>
        <select
          className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium"
          value={configuration.botUsername}
          onChange={handleUsernameTypeChange} // Use specific handler
          disabled={isDeploying || isDisabled}
        >
          <option value="random">Random Username</option>
          <option value="custom">Custom Username</option>
          <option value="premium">Premium Account</option>
        </select>
      </div>

      {/* --- NEW: Conditional Custom Username Input --- */}
      <AnimatePresence>
        {configuration.botUsername === 'custom' && (
          <motion.div
            key="custom-username-input"
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: '1.25rem' }} // Adjust margin as needed (mt-5)
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="transform-gpu transition-medium hover:scale-[1.02] overflow-hidden" // Add overflow-hidden for height animation
          >
            <label htmlFor="custom-bot-username" className="block text-xs text-gray-400 mb-1">Enter Custom Username</label>
            <div className="relative group">
              <i className={`fas fa-user absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-accent-${accentColor} transition-colors`}></i>
              <input
                id="custom-bot-username"
                type="text"
                placeholder="Enter desired username"
                value={configuration.customBotUsername || ''}
                onChange={(e) => handleChange('customBotUsername', e.target.value)}
                className={`w-full bg-dark-500 rounded-lg py-2 px-4 pl-10 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-${accentColor}/50 hover:border-dark-300 transition-medium`}
                disabled={isDeploying || isDisabled}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* --- End Custom Username Input --- */}


      {/* Attack Method */}
      <div className="transform-gpu transition-medium hover:scale-[1.02]">
        <label className="block text-xs text-gray-400 mb-1">Attack Method</label>
        <select
          className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium"
          value={configuration.attackMethod}
          onChange={(e) => handleChange('attackMethod', e.target.value)}
          disabled={isDeploying || isDisabled}
        >
          <option value="console-exploit">Console Commands Exploit</option>
          <option value="plugin-exploit">Plugin Vulnerabilities</option>
          <option value="social-engineering">Social Engineering</option>
          <option value="permission-exploit">Permissions Exploits</option>
          <option value="all">Try All Methods</option>
        </select>
      </div>

      {/* Bot Skin */}
      <div className="transform-gpu transition-medium hover:scale-[1.02]">
        <label className="block text-xs text-gray-400 mb-1">Bot Skin</label>
        <select
          className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium"
          value={configuration.botSkin}
          onChange={(e) => handleChange('botSkin', e.target.value)}
          disabled={isDeploying || isDisabled}
        >
          {['MHF_Steve', 'Notch', 'Herobrine', 'Technoblade', 'Alex', 'Dream'].map(skin => (
            <option key={skin} value={skin}>{skin}</option>
          ))}
        </select>
        {configuration.botSkin && (
          <div className="mt-2 flex justify-center items-center bg-dark-500/50 p-2 rounded-lg border border-dark-400">
            <img
              src={`https://mc-heads.net/avatar/${configuration.botSkin}/32`}
              alt={`${configuration.botSkin} skin preview`}
              className="w-8 h-8 rounded minecraft-head mr-2"
            />
            <span className='text-xs text-gray-400'>Preview</span>
          </div>
        )}
      </div>

      {/* Bot Behavior */}
      <div className="transform-gpu transition-medium hover:scale-[1.02]">
        <label className="block text-xs text-gray-400 mb-1">Bot Behavior</label>
        <select
          className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium"
          value={configuration.botBehavior}
          onChange={(e) => handleChange('botBehavior', e.target.value)}
          disabled={isDeploying || isDisabled}
        >
          <option value="stealth">Stealth Mode (Low Detection)</option>
          <option value="aggressive">Aggressive Mode (Fast Attack)</option>
          <option value="mixed">Mixed Mode (Balanced)</option>
        </select>
      </div>

      {/* Anti-Bot Bypass Method */}
      <div className="transform-gpu transition-medium hover:scale-[1.02]">
        <label className="block text-xs text-gray-400 mb-1">Anti-Bot Bypass Method</label>
        <select
          className="w-full bg-dark-500 rounded-lg py-2 px-3 text-sm border border-dark-400 focus:outline-none focus:ring-1 focus:ring-accent-purple/50 hover:border-dark-300 transition-medium"
          value={configuration.antiBotBypassMethod || 'none'}
          onChange={(e) => handleChange('antiBotBypassMethod', e.target.value)}
          disabled={isDeploying || isDisabled}
        >
          <option value="none">None (Default)</option>
          <option value="stealth_basic">Basic Stealth Tactics</option>
          <option value="proxy_rotation">Proxy Rotation</option>
          <option value="human_emulation">Human-like Behavior Emulation</option>
          <option value="advanced_evasion">Advanced Evasion Protocol</option>
        </select>
      </div>

      {/* Number of Attempts */}
      <div className="transform-gpu transition-medium hover:scale-[1.02]">
        <label className="block text-xs text-gray-400 mb-1 flex justify-between items-center">
          <span>Number of Attempts</span>
          <span className={`text-accent-${accentColor}`}>{configuration.numAttempts}</span>
        </label>
        <input
          type="range"
          min="10" max="500" step="10"
          value={configuration.numAttempts}
          onChange={(e) => handleChange('numAttempts', parseInt(e.target.value))}
          disabled={isDeploying || isDisabled}
          className={`w-full h-2 bg-dark-400 rounded-lg appearance-none cursor-pointer hover:bg-dark-300 transition-medium focus:outline-none focus:ring-1 focus:ring-accent-${accentColor}/50 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-accent-${accentColor} [&::-webkit-slider-thumb]:cursor-pointer hover:[&::-webkit-slider-thumb]:scale-110 [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-150 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-accent-${accentColor} [&::-moz-range-thumb]:cursor-pointer`}
        />
        <div className="flex justify-between text-xs mt-1 text-gray-500">
          <span>10</span>
          <span>500</span>
        </div>
        <p className="text-xs text-gray-400 mt-1 hover:text-gray-300 transition-medium">Higher values increase chances but may trigger detection.</p>
      </div>
    </div>

    {/* Deploy Button (Pushed to bottom with mt-auto) */}
    <div className="pt-4 border-t border-dark-400 mt-auto"> {/* Add mt-auto */}
      <button
        onClick={onDeploy}
        disabled={isDeploying || isDisabled} // Disable if deploying OR not connected
        className={`w-full py-3 bg-gradient-to-r from-accent-${accentColor} to-accent-pink text-white rounded-xl hover:opacity-90 transition font-medium transform-gpu hover:-translate-y-0.5 hover:shadow-glow active:translate-y-0 active:shadow-inner disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50 disabled:hover:shadow-none`}
      >
        {isDeploying ? (
          <> <i className="fas fa-circle-notch animate-spin mr-2"></i> Deploying... </>
        ) : (
          <> <i className="fas fa-robot mr-2"></i> Deploy {selectedBot.name} </>
        )}
      </button>
    </div>
  </div>
);
};

export default ConfigurationPanel;
